import { useMemo, useState, useCallback } from 'react';
import { addDays, addYears, format, isValid, parseISO } from 'date-fns';
import { DatePicker } from '@app/app/_components/DatePicker/DatePicker';
import { CheckmarkCircleIcon } from '@app/app/_icons/CheckmarkCircleIcon';
import { useTranslations } from 'next-intl';
import { Card } from '@app/app/_components/Card/Card';
import { Heading } from '@app/app/_components/Heading/Heading';

interface Props {
    initialBeginDate?: string;
}

export function InsuranceStartDateStep({ initialBeginDate }: Props) {
    const t = useTranslations();

    const minDate = useMemo(() => addDays(new Date(), 3), []);

    const maxDate = useMemo(() => addDays(minDate, 29), [minDate]);

    const defaultBeginDate = useMemo(() => {
        if (!initialBeginDate) return minDate;

        const parsed = parseISO(initialBeginDate);
        if (!isValid(parsed) || parsed < minDate || parsed > maxDate) return minDate;

        return parsed;
    }, [initialBeginDate, minDate, maxDate]);

    const [selectedBeginDate, setSelectedBeginDate] = useState<Nullable<Date>>(defaultBeginDate);

    const endDate = useMemo(() => {
        if (!selectedBeginDate) return null;

        return addDays(addYears(selectedBeginDate, 1), -1);
    }, [selectedBeginDate]);

    const formattedEndDate = useMemo(() => {
        return endDate ? format(endDate, 'yyyy-MM-dd') : '';
    }, [endDate]);

    const displayEndDate = useMemo(() => {
        return endDate ? format(endDate, 'yyyy-MM-dd') : '';
    }, [endDate]);

    const handleBeginDateChange = useCallback((date: Nullable<Date>) => {
        setSelectedBeginDate(date);
    }, []);

    return (
        <Card className="p-6">
            <div className="space-y-4">
                <Heading level="h4">{t('eshop.home.insuranceStartDateHeading')}</Heading>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <DatePicker
                        name="beginDate"
                        label={t('eshop.home.chooseDate')}
                        showResetButton={false}
                        placeholder={t('eshop.home.chooseDate')}
                        defaultValue={defaultBeginDate}
                        minDate={minDate}
                        maxDate={maxDate}
                        onChange={handleBeginDateChange}
                        alternateFormatting={true}
                    />
                    <input type="hidden" name="endDate" value={formattedEndDate} />

                    {selectedBeginDate && endDate && (
                        <div className="inline-flex items-center gap-2 font-medium md:mt-7">
                            <CheckmarkCircleIcon className="size-4 text-primary-400" />
                            <span>
                                <span className="text-neutral-500">{t('eshop.home.insuranceValidTill')}</span>
                                &nbsp;
                                <span>{displayEndDate}</span>
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
}
